import {
  Box,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { SearchIcon } from '@chakra-ui/icons'
import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'

import type { TemplateItem } from '@/store/inbox'

type Props = {
  trigger: ReactElement
  templates: TemplateItem[]
  onPick: (text: string) => void
}

export const TemplatePicker = ({ trigger, templates, onPick }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [query, setQuery] = useState('')
  const [tabIndex, setTabIndex] = useState(0)

  const quickTemplates = useMemo(() => {
    const q = query.trim().toLowerCase()
    return templates.filter((t) => {
      if (t.kind !== 'quick') return false
      if (!q) return true
      return t.title.toLowerCase().includes(q) || t.body.toLowerCase().includes(q)
    })
  }, [templates, query])

  const fullTemplates = useMemo(() => {
    const q = query.trim().toLowerCase()
    return templates.filter((t) => {
      if (t.kind !== 'full') return false
      if (!q) return true
      return t.title.toLowerCase().includes(q) || t.body.toLowerCase().includes(q)
    })
  }, [templates, query])

  const handlePick = (text: string) => {
    onPick(text)
    onClose()
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="top-start"
      closeOnBlur
      closeOnEsc
      gutter={10}
    >
      <PopoverTrigger>{trigger}</PopoverTrigger>

      <PopoverContent
        w={{ base: 'calc(100vw - 24px)', sm: '560px' }}
        maxW="560px"
        borderRadius="18px"
        bg="white"
        boxShadow="0 10px 28px rgba(15, 23, 42, 0.10)"
        border="1px solid"
        borderColor="gray.200"
        overflow="hidden"
      >
        <PopoverBody p="14px">
          <VStack spacing="12px" align="stretch">
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none" h="36px">
                <SearchIcon color="gray.400" boxSize="14px" />
              </InputLeftElement>

              <Input
                h="36px"
                placeholder="Search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                bg="white"
                borderColor="gray.200"
                _hover={{ borderColor: 'gray.300' }}
                _focusVisible={{ borderColor: 'gray.300', boxShadow: 'none' }}
                borderRadius="12px"
                fontSize="sm"
                color="gray.700"
              />
            </InputGroup>

            {/* Tabs (segmented, light like ref) */}
            <Tabs
              index={tabIndex}
              onChange={(i) => {
                setTabIndex(i)
                setQuery('')
              }}
              variant="unstyled"
            >
              <TabList gap="10px">
                <Tab
                  h="32px"
                  px="18px"
                  borderRadius="6px"
                  border="1px solid"
                  borderColor="gray.200"
                  bg="white"
                  fontWeight="600"
                  fontSize="sm"
                  color="gray.800"
                  _hover={{ bg: 'gray.50' }}
                  _selected={{ bg: 'gray.100', borderColor: 'gray.200' }}
                >
                  Quick Reply
                </Tab>

                <Tab
                  h="32px"
                  px="18px"
                  borderRadius="6px"
                  border="1px solid"
                  borderColor="gray.200"
                  bg="white"
                  fontWeight="600"
                  fontSize="sm"
                  color="gray.800"
                  _hover={{ bg: 'gray.50' }}
                  _selected={{ bg: 'gray.100', borderColor: 'gray.200' }}
                >
                  Full Template
                </Tab>
              </TabList>

              <TabPanels pt="10px">
                <TabPanel px="0" py="0">
                  <TemplateList items={quickTemplates} onPick={handlePick} />
                </TabPanel>

                <TabPanel px="0" py="0">
                  <TemplateList items={fullTemplates} onPick={handlePick} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

const TemplateList = ({
  items,
  onPick,
}: {
  items: TemplateItem[]
  onPick: (text: string) => void
}) => {
  if (!items.length) {
    return (
      <Box py="8px">
        <Text fontSize="sm" color="gray.500">
          No templates found.
        </Text>
      </Box>
    )
  }

  return (
    <Box maxH="320px" overflowY="auto" pr="2px">
      <List spacing="0">
        {items.map((t, idx) => (
          <Box key={t.id}>
            <ListItem
              py="10px"
              px="6px"
              borderRadius="8px"
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
              onClick={() => onPick(t.body)}
            >
              <Text fontWeight="700" fontSize="sm" mb="2px" color="gray.900">
                {t.title}
              </Text>
              <Text fontSize="sm" color="gray.600" noOfLines={1}>
                {t.body}
              </Text>
            </ListItem>

            {idx !== items.length - 1 ? (
              <Divider borderColor="gray.100" />
            ) : null}
          </Box>
        ))}
      </List>
    </Box>
  )
}
